import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './FormBuy.css';
import {
    CheckIcon,
    PackageIcon,
    SparklesIcon,
    ShieldCheckIcon,
    TruckIcon,
    RotateCcwIcon,
    UserIcon,
    PhoneIcon,
    MapPinIcon,
    HomeIcon,
    CartIcon
} from './Icons';
import { dbData } from './data';

const VARIANTS = [
    { id: 1, name: 'Blue', color: '#1a56db', productKey: 'blue', arName: 'أزرق' },
    { id: 2, name: 'Dark Pink', color: '#e91e8c', productKey: 'pink', arName: 'وردي غامق' },
    { id: 3, name: 'Red', color: '#e53935', productKey: 'red', arName: 'أحمر' },
    { id: 4, name: 'White', color: '#f0f0f0', productKey: 'white', arName: 'أبيض' },
];

const CITIES_KEYS = [
    'casablanca', 'rabat', 'marrakech', 'agadir', 'tanger', 'fes', 'meknes', 'oujda',
    'kenitra', 'tetouan', 'safi', 'mohammedia', 'khouribga', 'beni_mellal', 'el_jadida', 
    'taza', 'nador', 'settat', 'ksar_el_kebir', 'larache', 'khemisset', 'guelmim', 
    'berrechid', 'taourirt', 'dakhla', 'essaouira', 'tiznit', 'taroudant', 'kalaat_sraghna', 
    'sidi_kacem', 'laayoune', 'youssoufia', 'sefrau', 'ouarzazate', 'tiznit'
];

function SuccessScreen({ name, product, setPage, setSubmitted }) {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language === 'ar' ? 'ar' : (i18n.language === 'fr' ? 'fr' : 'en');

    return (
        <div className="success-screen">
            <div className="success-icon-wrapper">
                <div className="success-icon-bg"></div>
                <div className="success-main-icon"><TruckIcon /></div>
                <div className="success-check-badge"><CheckIcon /></div>
            </div>

            <h2>{t('form.orderConfirmed')}</h2>
            <p className="success-msg">{t('form.successMsg', { name })}</p>

            <div className="success-product-card">
                <img src={product.image} alt="Product" />
                <div className="success-product-details">
                    <strong>{product[currentLang]?.name || product.en.name}</strong>
                    <span className="success-price">{product.price} {t('common.mad')}</span>
                </div>
            </div>

            <div className="success-actions">
                <button className="success-btn primary shine-button" onClick={() => setSubmitted(false)}>
                    <SparklesIcon /> {t('form.orderMore')}
                </button>
                <button className="success-btn secondary" onClick={() => setPage('home')}>
                    <HomeIcon /> {t('form.backHome')}
                </button>
            </div>

            <p className="success-delivery-note">
                <PackageIcon /> {t('form.deliveryNote')}
            </p>
        </div>
    );
}

function FormBuy({ setPage, activeKey, showToast, initialBundle }) {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language === 'ar' ? 'ar' : (i18n.language === 'fr' ? 'fr' : 'en');

    const [product, setProduct] = useState(null);
    const [isBundle, setIsBundle] = useState(!!initialBundle);
    const [quantity, setQuantity] = useState(1);
    const [selectedCity, setSelectedCity] = useState('');
    const [citySearchTerm, setCitySearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [customerName, setCustomerName] = useState('');
    const [currentKey, setCurrentKey] = useState(activeKey);
    const [loading, setLoading] = useState(false);

    const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxMnbToGzcvT6_JADJ3A1u2T9O1w-OFAzndzMOe0lPF6PaqU565zyuuMB9Yx59eCzQRaQ/exec";
    const [isLoaded, setIsLoaded] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        setIsLoaded(false);
        if (initialBundle) {
            const bundleData = {
                id: initialBundle.id,
                price: initialBundle.price,
                oldPrice: initialBundle.oldPrice,
                image: initialBundle.image,
                en: { name: initialBundle.name, type: initialBundle.subtitle, benefits: initialBundle.items, size_ml: 'Pack' },
                fr: { name: initialBundle.name, type: initialBundle.subtitle, benefits: initialBundle.items, size_ml: 'Pack' },
                ar: { name: initialBundle.name, type: initialBundle.subtitle, benefits: initialBundle.items, size_ml: 'Pack' }
            };
            setProduct(bundleData);
            setIsBundle(true);
            setTimeout(() => setIsLoaded(true), 100);
        } else {
            setProduct(dbData.products[activeKey]);
            setCurrentKey(activeKey);
            setIsBundle(false);
            setTimeout(() => setIsLoaded(true), 100);
        }
    }, [activeKey, initialBundle]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!product) return (
        <div className="buy-loading">
            <div className="loading-ring"></div>
        </div>
    );

    if (submitted) return <SuccessScreen name={customerName} product={product} setPage={setPage} setSubmitted={setSubmitted} />;

    const content = product[currentLang] || product.en;
    const totalPrice = Number(product.price) * quantity;
    const savings = (Number(product.oldPrice) - Number(product.price)) * quantity;
    const activeVariant = isBundle ? null : VARIANTS.find(v => v.productKey === currentKey);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCity && !citySearchTerm) {
            showToast?.(t('form.selectCityError'), 'error');
            return;
        }

        setLoading(true);
        const startTime = Date.now();

        const name = e.target.fullname.value;
        const phone = e.target.phone.value;
        const address = e.target.address.value;

        const formData = {
            fullname: name,
            phone: phone,
            city: selectedCity ? t(`cities.${selectedCity}`) : citySearchTerm,
            address: address,
            productName: content.name,
            variant: isBundle ? 'Bundle' : (t(`common.variants.${currentKey}`) || currentKey),
            quantity: quantity,
            total: totalPrice,
            date: new Date().toLocaleString('fr-FR')
        };

        try {
            if (SCRIPT_URL) {
                await fetch(SCRIPT_URL, {
                    method: 'POST',
                    mode: 'no-cors',
                    cache: 'no-cache',
                    headers: { 'Content-Type': 'text/plain' },
                    body: JSON.stringify(formData)
                });
            }

            const endTime = Date.now();
            const elapsed = endTime - startTime;
            const remaining = Math.max(0, 6000 - elapsed);

            setTimeout(() => {
                setCustomerName(name);
                setSubmitted(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                showToast?.(t('form.successToast'));
            }, remaining);

        } catch (error) {
            console.error("Error submitting order:", error);
            showToast?.(t('form.errorToast'), 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`buy-container ${isLoaded ? 'loaded' : ''} ${i18n.language === 'ar' ? 'rtl' : ''}`}>
            <div className="form-nav-header">
                <button onClick={() => setPage('home')} className="back-link top">
                    {t('form.back')}
                </button>
            </div>
            <div className="product-info">
                <div className="product-info-inner">
                    <div
                        className="variant-stripe"
                        style={{ background: `linear-gradient(135deg, ${activeVariant?.color}22, transparent)` }}
                    ></div>

                    <div className="product-image-wrapper">
                        <div className="img-glow" style={{ background: `radial-gradient(circle, ${activeVariant?.color}33 0%, transparent 70%)` }}></div>
                        <img src={product.packImage || product.image} alt="product" />
                    </div>

                    <h2>{content.name}</h2>
                    <p className="product-type">{content.type} · {content.size_ml}ml</p>

                    <div className="product-price-tag">
                        <span className="current-price">{product.price} <small>{t('common.mad')}</small></span>
                        <span className="old-price-strikethrough">{product.oldPrice} {t('common.mad')}</span>
                    </div>

                    <div className="mini-specs">
                        {(content.benefits || []).slice(0, 3).map((b, i) => (
                            <span key={i} className="mini-tag"><SparklesIcon /> {b}</span>
                        ))}
                    </div>

                    <div className="trust-badges">
                        <div className="trust-badge"><ShieldCheckIcon /> {t('form.secure')}</div>
                        <div className="trust-badge"><TruckIcon /> COD</div>
                        <div className="trust-badge"><RotateCcwIcon /> {t('form.return')}</div>
                    </div>
                </div>
            </div>

            <div className="form-section">
                <div className="form-header">
                    <h2>{t('form.title')}</h2>
                    <p className="form-subtitle">{t('form.subtitle')}</p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="fullname">{t('form.fullName')}</label>
                            <div className="input-wrapper">
                                <span className="input-icon"><UserIcon /></span>
                                <input id="fullname" name="fullname" type="text" className="form-input" placeholder={i18n.language === 'ar' ? 'محمد العلمي' : 'Mohammed Alami'} required />
                            </div>
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">{t('form.phone')}</label>
                            <div className="input-wrapper">
                                <span className="input-icon"><PhoneIcon /></span>
                                <input id="phone" name="phone" type="tel" className="form-input" placeholder="+212 6XX XXX XXX" required />
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>{t('form.city')}</label>
                        <div className="custom-city-dropdown" ref={dropdownRef}>
                            <div
                                className={`city-trigger ${isDropdownOpen ? 'active' : ''} ${!selectedCity && !citySearchTerm ? 'placeholder' : ''}`}
                                onClick={() => setIsDropdownOpen(true)}
                            >
                                <span className="city-icon"><MapPinIcon /></span>
                                <input 
                                    type="text"
                                    className="city-search-input"
                                    placeholder={selectedCity ? t(`cities.${selectedCity}`) : t('form.selectCity')}
                                    value={citySearchTerm}
                                    onChange={(e) => {
                                        setCitySearchTerm(e.target.value);
                                        setIsDropdownOpen(true);
                                    }}
                                />
                                <span className={`arrow ${isDropdownOpen ? 'open' : ''}`} onClick={(e) => {
                                    e.stopPropagation();
                                    setIsDropdownOpen(!isDropdownOpen);
                                }}></span>
                            </div>
                            {isDropdownOpen && (
                                <ul className="city-menu">
                                    {CITIES_KEYS.filter(cityKey => 
                                        t(`cities.${cityKey}`).toLowerCase().includes(citySearchTerm.toLowerCase()) || 
                                        cityKey.toLowerCase().includes(citySearchTerm.toLowerCase())
                                    ).map(cityKey => (
                                        <li
                                            key={cityKey}
                                            className={selectedCity === cityKey ? 'selected' : ''}
                                            onClick={() => { 
                                                setSelectedCity(cityKey); 
                                                setCitySearchTerm('');
                                                setIsDropdownOpen(false); 
                                            }}
                                        >
                                            {selectedCity === cityKey && <span className="check"><CheckIcon /></span>}
                                            {t(`cities.${cityKey}`)}
                                        </li>
                                    ))}
                                    {CITIES_KEYS.filter(cityKey => 
                                        t(`cities.${cityKey}`).toLowerCase().includes(citySearchTerm.toLowerCase()) || 
                                        cityKey.toLowerCase().includes(citySearchTerm.toLowerCase())
                                    ).length === 0 && (
                                        <li style={{ justifyContent: 'center', pointerEvents: 'none', color: '#666' }}>
                                            {i18n.language === 'ar' ? 'لا توجد نتائج' : 'Aucun résultat'}
                                        </li>
                                    )}
                                </ul>
                            )}
                        </div>
                        <input type="hidden" value={selectedCity} required />
                    </div>

                    <div className="form-group">
                        <label htmlFor="address">
                            {t('form.address')} <span className="optional-tag">({i18n.language === 'ar' ? 'اختياري' : 'Optionnel'})</span>
                        </label>
                        <div className="input-wrapper">
                            <span className="input-icon"><HomeIcon /></span>
                            <textarea id="address" name="address" className="form-textarea" rows="2" placeholder={i18n.language === 'ar' ? 'الشارع، العمارة، الشقة...' : 'Street, building, apartment...'}></textarea>
                        </div>
                    </div>

                    <div className="qty-total-row">
                        <div className="form-group qty-group">
                            <label>{t('form.quantity')}</label>
                            <div className="quantity-control">
                                <button type="button" className="qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
                                <span className="qty-value">{quantity}</span>
                                <button type="button" className="qty-btn plus" onClick={() => setQuantity(q => q + 1)}>＋</button>
                            </div>
                        </div>

                        <div className="order-summary">
                            <div className="summary-row">
                                <span>{quantity} {quantity === 1 ? t('form.unit') : t('form.units')} × {product.price} {t('common.mad')}</span>
                                <span>{totalPrice} {t('common.mad')}</span>
                            </div>
                            <div className="summary-row savings">
                                <span>{t('form.save')}</span>
                                <span>−{savings} {t('common.mad')}</span>
                            </div>
                            <div className="summary-total">
                                <span>{t('form.total')}</span>
                                <span>{totalPrice} <small>{t('common.mad')}</small></span>
                            </div>
                        </div>
                    </div>

                    <button type="submit" className={`submit-btn ${loading ? 'loading' : ''} shine-button`} disabled={loading}>
                        {loading ? (
                            <div className="order-loader-container">
                                <div className="cart-loader-wrapper">
                                    <CartIcon />
                                </div>
                            </div>
                        ) : (
                            <>
                                <CartIcon />
                                {t('form.confirmOrder')}
                            </>
                        )}
                    </button>

                    <div className="trust-seals">
                        <div className="trust-seal">
                            <ShieldCheckIcon />
                            <span>{t('form.securePayment')}</span>
                        </div>
                        <div className="trust-seal">
                            <TruckIcon />
                            <span>{t('form.deliveryFast')}</span>
                        </div>
                        <div className="trust-seal">
                            <RotateCcwIcon />
                            <span>{t('form.return14')}</span>
                        </div>
                    </div>
                </form>

                <button onClick={() => setPage('home')} className="back-link">
                    {t('form.back')}
                </button>
            </div>
        </div>
    );
}

export default FormBuy;
